import { parseJson } from '@medicusunion/front-utils';
import { App } from 'antd';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDoctorProfile } from 'pages/DoctorProfile/DoctorProfileContext';

type Params = {
  id: string;
  lang: string;
};

const REQUIRED_FIELDS = [
  'first_name',
  'last_name',
  'gender',
  'timezone',
  'phone',
  'address',
  'specialties',
  'languages'
] as const;

/**
 * Reads the doctor profile from DoctorProfileContext (populated by the
 * parent DoctorProfile page's useEffect-driven fetch). Replaces the
 * legacy Apollo cache-only useQuery(GET_DOCTOR_PROFILE) read.
 */
const useDoctorProfileCache = ({ id, lang }: Params) => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const { profile } = useDoctorProfile();

  const validateProfile = useCallback(
    (cb: () => void) => {
      const isProfileCompleted = REQUIRED_FIELDS.every(key => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const field = (profile as any)?.[key];

        if (typeof field === 'string') {
          const parsed = parseJson(field);

          if (typeof parsed === 'number') {
            return true;
          }

          return parseJson(field)?.length;
        }

        if (Array.isArray(field)) {
          return field.length;
        }

        if (typeof field === 'number') {
          return true;
        }

        return !!field;
      });

      if (isProfileCompleted) {
        return cb();
      }

      modal.error({
        title: 'Error',
        content:
          'Please complete all profile information fields as a first step',
        onOk() {
          navigate(`/access/doctors/${id}/personal-information?lang=${lang}`);
        }
      });
    },
    [modal, profile, navigate, id, lang]
  );

  return { validateProfile, profile };
};

export default useDoctorProfileCache;
